import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import './Temporadas.css';
import { descargarPartidosExcel, descargarParticipantesExcel } from '../../utils/download';
import { proponerResultado, confirmarPartido, corregirPartido } from '../../services/partidosService';

const COLUMNAS_REQUERIDAS = [
  'Jornada', 'Modalidad', 'Local', 'Visitante',
  'Set 1 Local', 'Set 1 Visitante',
];

const esDobles = (modalidad) => modalidad.includes('DOBLES');

// PENDIENTE -> proponer + confirmar (individual: sin alineación no hace
// falta, se confirma del todo). En dobles se deja en PROPUESTO: confirmar
// exige alineación registrada, y esta pantalla no la recoge — hay que
// terminarlo desde Actas. Si ya había un resultado (PROPUESTO/CONFIRMADO),
// se corrige sin tocar el estado.
async function guardarResultado(partidoId, estadoActual, sets, modalidad) {
  if (estadoActual === 'PENDIENTE') {
    await proponerResultado(partidoId, sets);
    if (!esDobles(modalidad)) {
      await confirmarPartido(partidoId);
    }
  } else {
    await corregirPartido(partidoId, { sets });
  }
}

function validarEstructura(filas) {
  if (!filas.length) return ['El archivo está vacío o no tiene filas de datos.'];
  const cabeceras = Object.keys(filas[0]);
  const faltantes = COLUMNAS_REQUERIDAS.filter(c => !cabeceras.includes(c));
  if (faltantes.length) {
    return [
      'Faltan las siguientes columnas obligatorias:',
      ...faltantes.map(c => `  • ${c}`),
      '',
      `Columnas encontradas en el archivo: ${cabeceras.join(', ') || '(ninguna)'}`,
    ];
  }
  return [];
}

function validarFilas(filas, jornadas) {
  const errores = [];
  filas.forEach((f, i) => {
    const nFila = i + 2;
    const ref = `Fila ${nFila}`;

    const vacios = ['Jornada', 'Modalidad', 'Local', 'Visitante']
      .filter(c => f[c] === undefined || f[c] === null || String(f[c]).trim() === '');
    if (vacios.length) {
      errores.push(`${ref}: campo${vacios.length > 1 ? 's' : ''} vac${vacios.length > 1 ? 'ios' : 'io'} — ${vacios.join(', ')}.`);
      return;
    }

    const set1L = Number(f['Set 1 Local'] ?? 0);
    const set1V = Number(f['Set 1 Visitante'] ?? 0);
    const set2L = Number(f['Set 2 Local'] ?? 0);
    const set2V = Number(f['Set 2 Visitante'] ?? 0);
    const set3L = Number(f['Set 3 Local'] ?? 0);
    const set3V = Number(f['Set 3 Visitante'] ?? 0);
    const haySet1 = set1L > 0 || set1V > 0;
    const haySet2 = set2L > 0 || set2V > 0;
    const haySet3 = set3L > 0 || set3V > 0;

    if (!haySet1) {
      errores.push(`${ref} (${f['Local']} vs ${f['Visitante']}): falta el Set 1.`);
      return;
    }
    if (haySet2 && !haySet1) {
      errores.push(`${ref} (${f['Local']} vs ${f['Visitante']}): Set 2 relleno con Set 1 vacío.`);
      return;
    }
    if (haySet3 && !haySet2) {
      errores.push(`${ref} (${f['Local']} vs ${f['Visitante']}): Set 3 relleno con Set 2 vacío.`);
      return;
    }
    if (haySet3) {
      const ganSet1 = set1L > set1V ? 'local' : set1V > set1L ? 'visitante' : null;
      const ganSet2 = set2L > set2V ? 'local' : set2V > set2L ? 'visitante' : null;
      if (ganSet1 !== null && ganSet2 !== null && ganSet1 === ganSet2) {
        errores.push(
          `${ref} (${f['Local']} vs ${f['Visitante']}): Set 3 no válido — ` +
          `un mismo equipo ya ganó los dos primeros sets.`
        );
        return;
      }
    }

    const existe = jornadas.some(j =>
      j.modalidad === String(f['Modalidad']).trim() &&
      Number(j.jornada) === Number(f['Jornada']) &&
      j.local_nombre === String(f['Local']).trim() &&
      j.visitante_nombre === String(f['Visitante']).trim()
    );
    if (!existe) {
      const jornadaMod = jornadas.filter(j =>
        j.modalidad === String(f['Modalidad']).trim() &&
        Number(j.jornada) === Number(f['Jornada'])
      );
      if (!jornadaMod.length) {
        errores.push(
          `${ref}: no existe la jornada ${f['Jornada']} para la modalidad "${f['Modalidad']}".`
        );
      } else {
        const equiposEsperados = jornadaMod.map(j => `${j.local_nombre} vs ${j.visitante_nombre}`).join(', ');
        errores.push(
          `${ref}: "${f['Local']} vs ${f['Visitante']}" no coincide con ningun partido ` +
          `en jornada ${f['Jornada']} (${f['Modalidad']}). ` +
          `Partidos esperados: ${equiposEsperados}.`
        );
      }
    }
  });
  return errores;
}

function parsearFilas(filas, jornadas) {
  return filas.map(f => {
    const partido = jornadas.find(j =>
      j.modalidad === String(f['Modalidad']).trim() &&
      Number(j.jornada) === Number(f['Jornada']) &&
      j.local_nombre === String(f['Local']).trim() &&
      j.visitante_nombre === String(f['Visitante']).trim()
    );
    if (!partido) return null;

    const pares = [
      [Number(f['Set 1 Local'] ?? 0), Number(f['Set 1 Visitante'] ?? 0)],
      [Number(f['Set 2 Local'] ?? 0), Number(f['Set 2 Visitante'] ?? 0)],
      [Number(f['Set 3 Local'] ?? 0), Number(f['Set 3 Visitante'] ?? 0)],
    ];
    const setsConNumero = pares
      .map(([local, visitante], i) => ({ numero: i + 1, local, visitante }))
      .filter(({ local, visitante }) => local > 0 || visitante > 0);

    if (!setsConNumero.length) return null;

    let pL = 0, pV = 0;
    setsConNumero.forEach(s => { if (s.local > s.visitante) pL++; else if (s.visitante > s.local) pV++; });

    return {
      id: partido.id,
      estado: partido.estado,
      local: String(f['Local']).trim(),
      visitante: String(f['Visitante']).trim(),
      jornada: f['Jornada'],
      modalidad: String(f['Modalidad']).trim(),
      sets: setsConNumero.map(({ numero, local, visitante }) => ({
        numero, puntosLocal: local, puntosVisitante: visitante,
      })),
      previstoPuntos: { local: pL, visitante: pV },
      previstoGanador: pL > pV ? String(f['Local']).trim() : pV > pL ? String(f['Visitante']).trim() : null,
    };
  }).filter(Boolean);
}

function ModalEditarPartido({ partido, onGuardar, onClose }) {
  const setDe = (n) => partido.sets?.find(s => s.numero === n);
  const [sets, setSets] = useState([
    { local: setDe(1)?.puntosLocal ?? '', visitante: setDe(1)?.puntosVisitante ?? '' },
    { local: setDe(2)?.puntosLocal ?? '', visitante: setDe(2)?.puntosVisitante ?? '' },
    { local: setDe(3)?.puntosLocal ?? '', visitante: setDe(3)?.puntosVisitante ?? '' },
  ]);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const actualizar = (i, lado, val) =>
    setSets(prev => prev.map((s, idx) => idx === i ? { ...s, [lado]: val } : s));

  const guardar = async () => {
    setError(null);
    const haySet = sets.map(s => Number(s.local) > 0 || Number(s.visitante) > 0);
    if (!haySet[0]) { setError('Introduce al menos el Set 1.'); return; }
    if (haySet[1] && !haySet[0]) { setError('Set 2 relleno con Set 1 vacío.'); return; }
    if (haySet[2] && !haySet[1]) { setError('Set 3 relleno con Set 2 vacío.'); return; }
    if (haySet[2]) {
      const g1 = Number(sets[0].local) > Number(sets[0].visitante) ? 'l' : 'v';
      const g2 = Number(sets[1].local) > Number(sets[1].visitante) ? 'l' : 'v';
      if (g1 === g2) { setError('Set 3 no válido — un mismo equipo ya ganó los dos primeros sets.'); return; }
    }

    const setsValidos = sets
      .map((s, i) => ({ numero: i + 1, puntosLocal: Number(s.local), puntosVisitante: Number(s.visitante) }))
      .filter((_, i) => haySet[i]);

    setGuardando(true);
    try {
      await onGuardar(partido.id, partido.estado, setsValidos, partido.modalidad);
      onClose();
    } catch (err) {
      setError(err.message || 'Error al guardar.');
      setGuardando(false);
    }
  };

  const thStyle = { padding: '6px 10px', textAlign: 'center', fontWeight: 600, fontSize: '.82rem', color: 'var(--c-muted)', borderBottom: '2px solid var(--c-border)' };
  const tdStyle = { padding: '6px 8px', textAlign: 'center' };
  const inputStyle = { width: '60px', padding: '4px 6px', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-sm)', textAlign: 'center', fontSize: '.9rem', fontFamily: 'var(--font-base)' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Editar resultado</h3>
        <p style={{ color: 'var(--c-muted)', fontSize: '.88rem', margin: '0 0 1.25rem' }}>
          <strong>{partido.local_nombre}</strong> vs <strong>{partido.visitante_nombre}</strong>
          <span style={{ marginLeft: 8 }}>— J.{partido.jornada} · {partido.modalidad}</span>
        </p>
        {esDobles(partido.modalidad) && partido.estado === 'PENDIENTE' && (
          <p style={{ color: 'var(--c-muted)', fontSize: '.8rem', margin: '0 0 1rem' }}>
            Al guardar quedará "Propuesto": para confirmarlo hace falta registrar la alineación desde Actas.
          </p>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
          <thead>
            <tr>
              <th style={thStyle}>Set</th>
              <th style={thStyle}>{partido.local_nombre}</th>
              <th style={{ ...thStyle, color: '#bbb', width: 20 }}></th>
              <th style={thStyle}>{partido.visitante_nombre}</th>
            </tr>
          </thead>
          <tbody>
            {sets.map((s, i) => (
              <tr key={i}>
                <td style={{ ...tdStyle, color: 'var(--c-muted)', fontSize: '.82rem' }}>Set {i + 1}</td>
                <td style={tdStyle}>
                  <input style={inputStyle} type="number" min="0" value={s.local}
                    onChange={e => actualizar(i, 'local', e.target.value)} />
                </td>
                <td style={{ ...tdStyle, color: '#bbb' }}>–</td>
                <td style={tdStyle}>
                  <input style={inputStyle} type="number" min="0" value={s.visitante}
                    onChange={e => actualizar(i, 'visitante', e.target.value)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {error && <p className="error-msg" style={{ marginBottom: '1rem' }}>{error}</p>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ModalError({ titulo, lineas, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <h3 style={{ color: 'var(--c-danger)', marginTop: 0 }}>{titulo}</h3>
        <div style={{
          background: '#fdf0f0',
          border: '1px solid #f5c6cb',
          borderRadius: 'var(--radius-md)',
          padding: '.75rem 1rem',
          marginBottom: '1rem',
          maxHeight: '320px',
          overflowY: 'auto',
        }}>
          {lineas.map((l, i) => (
            <p key={i} style={{
              margin: '3px 0',
              fontSize: '.83rem',
              color: l === '' ? undefined : '#721c24',
              fontFamily: (l.startsWith('  ') || l.startsWith('Fila')) ? 'monospace' : 'inherit',
            }}>
              {l || ' '}
            </p>
          ))}
        </div>
        <p style={{ fontSize: '.8rem', color: 'var(--c-muted)', margin: '0 0 1.25rem' }}>
          La importacion solo actualiza partidos existentes — no crea partidos nuevos.
          Descarga la plantilla con <strong>Partidos Excel</strong>, corrigela y vuelve a subirla.
        </p>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

export default function DescargaTab({ temporada, jornadas, participantes, onRefresh, editable }) {
  const [modFiltro, setModFiltro] = useState('TODAS');
  const [jornadaFiltro, setJornadaFiltro] = useState('TODAS');
  const [preview, setPreview] = useState(null);
  const [importando, setImportando] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [errorModal, setErrorModal] = useState(null);
  const [partidoEditando, setPartidoEditando] = useState(null);
  const fileRef = useRef();

  const guardarEdicion = (id, estadoActual, sets, modalidad) =>
    guardarResultado(id, estadoActual, sets, modalidad).then(() => onRefresh?.());

  const modalidades = ['TODAS', ...new Set(jornadas.map(j => j.modalidad))];
  const numJornadas = jornadas.length ? Math.max(...jornadas.map(j => j.jornada)) : 0;

  const filtradas = jornadas.filter(j =>
    (modFiltro === 'TODAS' || j.modalidad === modFiltro) &&
    (jornadaFiltro === 'TODAS' || j.jornada === parseInt(jornadaFiltro))
  );

  const nombreBase = () => {
    const mod = modFiltro === 'TODAS' ? 'todas' : modFiltro.toLowerCase().replace(/ /g, '-');
    const jorn = jornadaFiltro === 'TODAS' ? 'completo' : `jornada-${jornadaFiltro}`;
    return `${temporada}_${mod}_${jorn}`;
  };

  const nombreGanador = (j) => {
    if (j.ganador_participante_id === j.local_participante_id) return j.local_nombre;
    if (j.ganador_participante_id === j.visitante_participante_id) return j.visitante_nombre;
    return '';
  };

  const descargarPartidos = async () => {
    const setDe = (j, n) => j.sets?.find(s => s.numero === n);
    const filas = filtradas.map(j => ({
      Temporada: j.temporada_id,
      Jornada: j.jornada,
      Tipo: j.tipo,
      Modalidad: j.modalidad,
      Local: j.local_nombre,
      Visitante: j.visitante_nombre,
      'Set 1 Local': setDe(j, 1)?.puntosLocal ?? '',
      'Set 1 Visitante': setDe(j, 1)?.puntosVisitante ?? '',
      'Set 2 Local': setDe(j, 2)?.puntosLocal ?? '',
      'Set 2 Visitante': setDe(j, 2)?.puntosVisitante ?? '',
      'Set 3 Local': setDe(j, 3)?.puntosLocal ?? '',
      'Set 3 Visitante': setDe(j, 3)?.puntosVisitante ?? '',
      Ganador: nombreGanador(j),
    }));
    await descargarPartidosExcel(filas, 'Partidos', `${nombreBase()}.xlsx`, temporada);
  };

  const descargarParticipantesFiltrados = () => {
    const lista = (participantes ?? []).filter(
      p => modFiltro === 'TODAS' || p.modalidad === modFiltro
    );
    const filas = lista.map(p => ({
      Modalidad: p.modalidad,
      Equipo: p.nombre_mostrar,
      Jugadores: p.jugadores.map(j => j.nombre).join(', '),
      Estado: p.estado ?? '',
    }));
    descargarParticipantesExcel(filas, 'Participantes', `${temporada}_participantes.xlsx`, temporada);
  };

  const leerExcel = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFeedback(null);
    setPreview(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const libro = XLSX.read(ev.target.result, { type: 'array' });
        const hoja = libro.Sheets[libro.SheetNames[0]];
        // La tabla empieza bajo la cabecera con logo; en plantillas antiguas, en la fila 1.
        const filaCabecera = XLSX.utils.sheet_to_json(hoja, { header: 1, blankrows: true })
          .findIndex(r => r[0] === 'Temporada');
        const filas = XLSX.utils.sheet_to_json(hoja, { range: Math.max(filaCabecera, 0) });

        const erroresEstructura = validarEstructura(filas);
        if (erroresEstructura.length) {
          setErrorModal({ titulo: 'Estructura incorrecta', lineas: erroresEstructura });
          return;
        }

        const erroresFilas = validarFilas(filas, jornadas);
        if (erroresFilas.length) {
          setErrorModal({
            titulo: `${erroresFilas.length} fila${erroresFilas.length > 1 ? 's' : ''} con error`,
            lineas: [
              'No se guardara ningun partido hasta corregir los siguientes problemas:',
              '',
              ...erroresFilas,
            ],
          });
          return;
        }

        setPreview(parsearFilas(filas, jornadas));
      } catch {
        setErrorModal({
          titulo: 'Error al leer el archivo',
          lineas: ['No se pudo leer el archivo. Asegurate de que es un Excel valido (.xlsx o .xls).'],
        });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const aplicarCambios = async () => {
    if (!preview?.length) return;
    setImportando(true);
    setFeedback(null);
    try {
      await Promise.all(preview.map(p => guardarResultado(p.id, p.estado, p.sets, p.modalidad)));
      const dobles = preview.some(p => esDobles(p.modalidad));
      setFeedback({
        tipo: 'ok',
        msg: `${preview.length} partido${preview.length > 1 ? 's' : ''} actualizado${preview.length > 1 ? 's' : ''}.`
          + (dobles ? ' Los de dobles quedan "Propuestos": confírmalos desde Actas tras registrar la alineación.' : ''),
      });
      setPreview(null);
      onRefresh?.();
    } catch (err) {
      setFeedback({ tipo: 'error', msg: err.message || 'Error al guardar.' });
    } finally {
      setImportando(false);
    }
  };

  return (
    <div>
      {errorModal && (
        <ModalError
          titulo={errorModal.titulo}
          lineas={errorModal.lineas}
          onClose={() => setErrorModal(null)}
        />
      )}
      {partidoEditando && (
        <ModalEditarPartido
          partido={partidoEditando}
          onGuardar={guardarEdicion}
          onClose={() => setPartidoEditando(null)}
        />
      )}

      <div className="descarga-form">
        <label>
          Modalidad
          <select value={modFiltro} onChange={e => setModFiltro(e.target.value)}>
            {modalidades.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label>
          Jornada
          <select value={jornadaFiltro} onChange={e => setJornadaFiltro(e.target.value)}>
            <option value="TODAS">Todas las jornadas</option>
            {Array.from({ length: numJornadas }, (_, i) => (
              <option key={i + 1} value={i + 1}>Jornada {i + 1}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        <button className="btn btn-primary" onClick={descargarPartidos} disabled={!filtradas.length}>
          Partidos Excel ({filtradas.length})
        </button>
        <button className="btn btn-secondary" onClick={descargarParticipantesFiltrados} disabled={!(participantes ?? []).length}>
          Participantes Excel
        </button>
      </div>

      {!jornadas.length && (
        <p className="rr-empty">Genera el Round Robin primero para poder descargar partidos.</p>
      )}

      {jornadas.length > 0 && (
        <div className="import-section">
          <h4 className="import-title">Importar resultados desde Excel</h4>
          <p className="import-desc">
            Descarga la plantilla, rellena los sets y vuelve a subir el archivo. Los partidos de
            dobles quedan "Propuestos" — hace falta registrar la alineación desde Actas para confirmarlos.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={leerExcel}
          />
          <button className="btn btn-secondary" onClick={() => fileRef.current.click()}>
            Seleccionar Excel
          </button>

          {preview && preview.length === 0 && (
            <p className="import-warn">No se encontraron partidos coincidentes en el archivo.</p>
          )}

          {preview && preview.length > 0 && (
            <div className="import-preview">
              <p className="import-preview-info">
                Se van a actualizar <strong>{preview.length}</strong> partido{preview.length > 1 ? 's' : ''}:
              </p>
              <table className="import-table">
                <thead>
                  <tr>
                    <th>J.</th><th>Modalidad</th><th>Local</th><th>Visitante</th><th>Sets</th><th>Ganador previsto</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.map((p, i) => (
                    <tr key={i}>
                      <td>{p.jornada}</td>
                      <td>{p.modalidad}</td>
                      <td>{p.local}</td>
                      <td>{p.visitante}</td>
                      <td>{p.sets.map(s => `${s.puntosLocal}-${s.puntosVisitante}`).join(' / ')}</td>
                      <td><strong>{p.previstoGanador ?? 'Empate'}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn btn-success" onClick={aplicarCambios} disabled={importando}>
                  {importando ? 'Guardando...' : `Guardar ${preview.length} cambio${preview.length > 1 ? 's' : ''}`}
                </button>
                <button className="btn btn-secondary" onClick={() => setPreview(null)}>
                  Cancelar
                </button>
              </div>
            </div>
          )}

          {feedback && (
            <p className={feedback.tipo === 'ok' ? 'success-msg' : 'error-msg'} style={{ marginTop: '0.75rem' }}>
              {feedback.msg}
            </p>
          )}
        </div>
      )}

      {filtradas.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem', marginTop: '1.5rem' }}>
          <thead>
            <tr style={{ background: '#f8f9fa' }}>
              {['J.', 'Tipo', 'Modalidad', 'Local', 'Visitante', 'Resultado', ...(editable ? [''] : [])].map((h, i) => (
                <th key={i} style={{ padding: '6px 8px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtradas.map((j) => (
              <tr key={j.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '5px 8px' }}>{j.jornada}</td>
                <td style={{ padding: '5px 8px' }}>
                  <span className={`tipo-badge${j.tipo === 'VUELTA' ? ' vuelta' : ''}`}>{j.tipo}</span>
                </td>
                <td style={{ padding: '5px 8px' }}>{j.modalidad}</td>
                <td style={{ padding: '5px 8px' }}>{j.local_nombre}</td>
                <td style={{ padding: '5px 8px' }}>{j.visitante_nombre}</td>
                <td style={{ padding: '5px 8px' }}>
                  {j.sets?.length
                    ? <span style={{ color: '#888' }}>
                        {j.sets_local}-{j.sets_visitante} ({j.sets.map(s => `${s.puntosLocal}-${s.puntosVisitante}`).join('/')})
                        {j.estado !== 'CONFIRMADO' && <span style={{ marginLeft: 6, fontSize: '.72rem', color: '#b8860b' }}>({j.estado})</span>}
                      </span>
                    : <span style={{ background: '#fff3cd', color: '#7d6608', fontSize: '.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10, letterSpacing: '0.03em' }}>Pendiente</span>}
                </td>
                {editable && (
                  <td style={{ padding: '5px 8px' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '2px 10px', fontSize: '.78rem' }}
                      onClick={() => setPartidoEditando(j)}
                    >
                      Editar
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
